'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import StrapiBlocksRenderer from '@/components/ui/StrapiBlocksRenderer';
import useStrapiHome from '@/hooks/useStrapiHome';

interface InfoCard {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  visible?: boolean;
  url?: string | null;
  imagen?: {
    id: number;
    name: string;
    url: string;
    formats?: {
      thumbnail?: { url: string };
      large?: { url: string };
      medium?: { url: string };
      small?: { url: string };
    };
  };
  backgroundColor?: string; // Mantenido para compatibilidad con diseño
}

interface InfoCardsWithRecipeProps {
  className?: string;
  cards?: InfoCard[];
}

const InfoCardsWithRecipe: React.FC<InfoCardsWithRecipeProps> = ({ 
  className = '',
  cards = []
}) => {
  const [selectedCard, setSelectedCard] = useState<number>(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const { data: strapiData, loading, error } = useStrapiHome();

  // Colores de fondo por defecto para las cards
  const defaultBackgrounds = ['bg-global-3', 'bg-global-4', 'bg-global-3'];

  // Procesar cards desde los datos de Strapi
  const strapiCards: InfoCard[] = strapiData?.infoCards ? 
    strapiData.infoCards
      .filter(card => card.visible !== false) // Filtrar cards visibles
      .map((card, index) => ({
        ...card,
        backgroundColor: defaultBackgrounds[index] || 'bg-global-3'
      })) : [];

  // Cards por defecto si no hay datos de Strapi
  const defaultCards: InfoCard[] = [
    {
      id: 1,
      title: '¿Eres profesional?',
      subtitle: 'Ut enim ad minim veniam',
      description: 'Descubre nuestros productos especiales para profesionales de la gastronomía.',
      backgroundColor: 'bg-global-3'
    },
    {
      id: 2,
      title: 'Recetas',
      subtitle: 'Deliciosas creaciones',
      description: 'Encuentra las mejores recetas con ingredientes auténticos italianos.',
      backgroundColor: 'bg-global-4'
    },
    {
      id: 3,
      title: 'Marcas',
      subtitle: 'Calidad garantizada',
      description: 'Conoce nuestras marcas premium directamente importadas de Italia.',
      backgroundColor: 'bg-global-3'
    }
  ];

  // Si hay datos de Strapi pero ninguna card es visible, no renderizar la sección
  if (strapiData?.infoCards && strapiData.infoCards.length > 0 && strapiCards.length === 0) {
    return null;
  }

  // Prioridad: props cards > Strapi cards > default cards
  const cardsToUse = cards.length > 0 ? cards : (strapiCards.length > 0 ? strapiCards : defaultCards);

  const handleCardHover = (index: number) => {
    if (index !== selectedCard) {
      setIsTransitioning(true);
      setSelectedCard(index);
      setTimeout(() => setIsTransitioning(false), 500);
    }
  };

  // Obtener la imagen de la card seleccionada
  const selectedCardData = cardsToUse[selectedCard];
  const selectedImageUrl = selectedCardData?.imagen?.url;

  if (loading) {
    return (
      <div data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:101:6" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="101" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`${className} flex flex-col gap-8`}>
        {/* Loading para Recipe Image */}
        <div data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:103:8" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="103" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%22%7D" className="w-full">
          <div data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:104:10" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="104" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20rounded-%5B20px%5D%20overflow-hidden%20h-%5B526px%5D%20bg-gray-300%20animate-pulse%22%7D" className="relative rounded-[20px] overflow-hidden h-[526px] bg-gray-300 animate-pulse"></div>
        </div>
        {/* Loading para Cards */}
        <div data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:107:8" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="107" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[1, 2, 3].map((i) => (
            <div data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:109:12" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="109" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-gray-300%20rounded-%5B20px%5D%20p-8%20animate-pulse%22%7D" key={i} className="bg-gray-300 rounded-[20px] p-8 animate-pulse">
              <div data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:110:14" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="110" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-6%20bg-gray-400%20rounded%20mb-4%20w-20%22%7D" className="h-6 bg-gray-400 rounded mb-4 w-20"></div>
              <div data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:111:14" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="111" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-5%20bg-gray-400%20rounded%20mb-2%20w-3%2F4%22%7D" className="h-5 bg-gray-400 rounded mb-2 w-3/4"></div>
              <div data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:112:14" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="112" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-4%20bg-gray-400%20rounded%20mb-1%20w-full%22%7D" className="h-4 bg-gray-400 rounded mb-1 w-full"></div>
              <div data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:113:14" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="113" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-4%20bg-gray-400%20rounded%20mb-1%20w-5%2F6%22%7D" className="h-4 bg-gray-400 rounded mb-1 w-5/6"></div>
              <div data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:114:14" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="114" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-4%20bg-gray-400%20rounded%20w-2%2F3%22%7D" className="h-4 bg-gray-400 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:123:4" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="123" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`${className} flex flex-col gap-8`}>
      {/* Recipe Image Section */}
      <div data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:125:6" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="125" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%22%7D" className="w-full">
        <div data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:126:8" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="126" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20rounded-%5B20px%5D%20overflow-hidden%20h-%5B526px%5D%22%7D" className="relative rounded-[20px] overflow-hidden h-[526px]">
          <div data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:127:10" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="127" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`transition-opacity duration-500 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
            <Image data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:128:12" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="128" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22object-cover%22%7D"
              src={selectedImageUrl || "/images/img_receta_1.png"}
              alt={selectedCardData?.title || "Recipe"}
              fill
              className="object-cover"
            />
          </div>
        </div>
      </div>

      {/* Cards Section */}
      <div data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:139:6" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="139" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {cardsToUse.map((card, index) => {
          const cardClassName = `${card.backgroundColor || 'bg-global-3'} rounded-[20px] p-8 cursor-pointer transition-all duration-300 hover:scale-[1.02] ${
            selectedCard === index ? 'ring-2 ring-[#3d1c1a]' : ''
          }`;

          const cardContent = (
            <>
              <div data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:147:14" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="147" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-global-9%20rounded%20px-4%20inline-block%20mb-4%22%7D" className="bg-global-9 rounded px-4 inline-block mb-4">
                <span data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:148:16" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="148" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-tt-norms-medium%20text-sm%20leading-4%20text-global-1%22%7D" className="font-tt-norms-medium text-sm leading-4 text-global-1">
                  {card.title}
                </span>
              </div>
              <h3 data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:152:14" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="152" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-tt-norms-extra-black%20text-base%20leading-5%20uppercase%20text-global-1%20mb-2%22%7D" className="font-tt-norms-extra-black text-base leading-5 uppercase text-global-1 mb-2">
                {card.subtitle}
              </h3>
              <p data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:155:14" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="155" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-tt-norms-medium%20text-base%20leading-%5B21px%5D%20text-global-1%22%7D" className="font-tt-norms-medium text-base leading-[21px] text-global-1">
                {card.description}
              </p>
            </>
          );

          if (card.url) {
            return (
              <Link data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:163:14" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="163" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22%5Bvar%3AcardClassName%5D%22%7D"
                key={card.id}
                href={card.url}
                className={cardClassName}
                onMouseEnter={() => handleCardHover(index)}
              >
                {cardContent}
              </Link>
            );
          }

          return (
            <div data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:175:12" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="175" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22%5Bvar%3AcardClassName%5D%22%7D"
              key={card.id}
              className={cardClassName}
              onMouseEnter={() => handleCardHover(index)}
            >
              {cardContent}
            </div>
          );
        })}
      </div>
      
      {error && (
        <div data-component-id="src/components/sections/InfoCardsWithRecipe.tsx:187:8" data-component-path="src/components/sections/InfoCardsWithRecipe.tsx" data-component-line="187" data-component-file="InfoCardsWithRecipe.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-red-500%20text-sm%20text-center%22%2C%22textContent%22%3A%22Error%20al%20cargar%20el%20contenido%3A%22%7D" className="text-red-500 text-sm text-center">
          Error al cargar el contenido: {error}
        </div>
      )}
    </div>
  );
};

export default InfoCardsWithRecipe;